import React, {
  Component,
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import RouteFallback from "../../Core/Loader/RouteFallback";
import { FLAGS, useFeatureFlag, useFeatureFlagsReady } from "../../featureFlags";
import { HomepageActiveContext } from "../../hooks/useHomepageActive";
import PremiumGate from "../SandboxPremium/Components/PremiumGate";
import useHomepageVersion from "../../hooks/useHomepageVersion";
import { HOMEPAGE_VERSION } from "../../utils/homepageVersion";
import useHomepageSlots from "./useHomepageSlots";
import { MAX_FLAG_WAIT_MS } from "./utils/constants";
import { HOMEPAGE_LOADERS } from "./utils/homepageLoaders";
import styles from "./style.module.css";

const HOMEPAGES = {
  [HOMEPAGE_VERSION.NEW]: lazy(HOMEPAGE_LOADERS[HOMEPAGE_VERSION.NEW]),
  [HOMEPAGE_VERSION.OLD]: lazy(HOMEPAGE_LOADERS[HOMEPAGE_VERSION.OLD]),
};

/**
 * A homepage kept loaded out of sight must never take the visible one down.
 * If the hidden slot throws, it renders nothing and reports it (the slot is
 * then dropped for this visit). If the shown slot throws, the error carries on
 * up exactly as it did before slots existed.
 */
class SlotErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch() {
    if (!this.props.isShown) this.props.onHiddenError();
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.isShown) throw this.state.error;
    return null;
  }
}

/** Stands in while a homepage's code loads, and tells the slot when that starts and ends. */
const SlotLoading = ({ isShown, onStart, onEnd }) => {
  useLayoutEffect(() => {
    onStart();
    return onEnd;
    // Mount and unmount mark the start and end of this load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isShown ? <RouteFallback className={styles.fill} /> : null;
};

/**
 * One homepage, shown or kept hidden. Hidden is display:none, so it takes no
 * space and can't be focused or read out. HomepageActiveContext (`isActive`)
 * tells the homepage itself, so it holds back popups, one-time hints and landing
 * analytics until it is the one on screen and any sweep to it is over.
 */
const HomepageSlot = ({
  version,
  isShown,
  isActive,
  onLoading,
  onLoaded,
  onHiddenError,
}) => {
  const Homepage = HOMEPAGES[version];
  const isLoadingRef = useRef(false);

  useLayoutEffect(() => {
    // Code already cached: nothing suspended, so it is ready as soon as it mounts.
    if (!isLoadingRef.current) onLoaded(version);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={isShown ? undefined : styles.hiddenSlot}>
      <SlotErrorBoundary
        isShown={isShown}
        onHiddenError={() => onHiddenError(version)}
      >
        <HomepageActiveContext.Provider value={isActive}>
          <Suspense
            fallback={
              <SlotLoading
                isShown={isShown}
                onStart={() => {
                  isLoadingRef.current = true;
                  onLoading(version);
                }}
                onEnd={() => {
                  isLoadingRef.current = false;
                  onLoaded(version);
                }}
              />
            }
          >
            <Homepage />
          </Suspense>
        </HomepageActiveContext.Provider>
      </SlotErrorBoundary>
    </div>
  );
};

const SwitchableHomepages = ({ version }) => {
  const {
    areaRef,
    shownVersion,
    activeVersion,
    mountedVersions,
    handleLoading,
    handleLoaded,
    handleHiddenError,
  } = useHomepageSlots({ version, lineClassName: styles.sweepLine });

  return (
    <div ref={areaRef} className={styles.area}>
      {/* the paywall and session writes the old homepage does itself */}
      <PremiumGate isActive={activeVersion === HOMEPAGE_VERSION.NEW} />
      {mountedVersions.map((slot) => (
        <HomepageSlot
          key={slot}
          version={slot}
          isShown={slot === shownVersion}
          isActive={slot === activeVersion}
          onLoading={handleLoading}
          onLoaded={handleLoaded}
          onHiddenError={handleHiddenError}
        />
      ))}
    </div>
  );
};

/**
 * The paid student's /home, behind the `sandbox_new_homepage` flag.
 * - Flag off: the old homepage, exactly as before the new one existed.
 * - Flag on, can choose (standalone Sandbox): both homepages kept ready and the
 *   banner sweep between them.
 * - Flag on, can't choose (StudentPortal's embedded Sandbox): just their homepage.
 * Wherever the new homepage is the one on screen, PremiumGate does what the old
 * homepage does for every paid student beyond its own UI: the Sandbox premium
 * paywall and the session writes (`linkedUser` for Evaluation).
 * Until the flag has its real value for this student — first load, or right
 * after an in-app login while Statsig fetches their values — it shows the route loader
 * rather than the old homepage, which would fetch, send `home_landed` and then
 * be swapped out — for at most MAX_FLAG_WAIT_MS, then it goes with what the
 * flag reads (a slow provider costs a late swap, not a blank landing page).
 */
const PaidStudentHome = () => {
  const isNewHomepageReleased = useFeatureFlag(FLAGS.SANDBOX_NEW_HOMEPAGE.name);
  const areFlagsReady = useFeatureFlagsReady();
  const [hasWaitedForFlags, setHasWaitedForFlags] = useState(false);
  const [wereFlagsReady, setWereFlagsReady] = useState(areFlagsReady);
  const { isNewHomepage, isChoiceAvailable } = useHomepageVersion();
  const version = isNewHomepage ? HOMEPAGE_VERSION.NEW : HOMEPAGE_VERSION.OLD;

  // Not ready again (the flags are switching to a newly signed-in student): wait
  // again, with a fresh cap. Reset during render, not in an effect, so no
  // homepage mounts for one render (and starts its calls) before the loader.
  if (wereFlagsReady !== areFlagsReady) {
    setWereFlagsReady(areFlagsReady);
    if (!areFlagsReady) setHasWaitedForFlags(false);
  }

  useEffect(() => {
    if (areFlagsReady) return undefined;
    // While the flag is off — everyone, until release — the old homepage is what
    // comes next: fetch its code during the wait rather than after it.
    HOMEPAGE_LOADERS[HOMEPAGE_VERSION.OLD]().catch(() => {});
    const timerId = setTimeout(() => setHasWaitedForFlags(true), MAX_FLAG_WAIT_MS);
    return () => clearTimeout(timerId);
  }, [areFlagsReady]);

  if (!areFlagsReady && !hasWaitedForFlags) {
    return <RouteFallback className={styles.fill} />;
  }

  if (!isNewHomepageReleased) {
    const OldHomepage = HOMEPAGES[HOMEPAGE_VERSION.OLD];
    return <OldHomepage />;
  }

  if (!isChoiceAvailable) {
    const Homepage = HOMEPAGES[version];
    if (version === HOMEPAGE_VERSION.OLD) return <Homepage />;
    return (
      <>
        <Homepage />
        <PremiumGate isActive />
      </>
    );
  }

  return <SwitchableHomepages version={version} />;
};

export default PaidStudentHome;
